import React, { createContext, useContext, useEffect, useState } from 'react';
import appleAuthService from '@/services/appleAuth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 从本地存储加载用户信息
    const hasStoredUser = appleAuthService.loadFromStorage();
    if (hasStoredUser) {
      setUser(appleAuthService.getCurrentUser());
      setToken(appleAuthService.getCurrentToken());
    }
    setLoading(false);

    // 添加状态变化监听器
    const handleAuthChange = (currentUser, currentToken) => {
      setUser(currentUser);
      setToken(currentToken);
    };

    appleAuthService.addListener(handleAuthChange);

    return () => {
      appleAuthService.removeListener(handleAuthChange);
    };
  }, []);

  const login = async (authData) => {
    try {
      console.log('🔑 AuthContext login called with:', authData);
      setLoading(true);
      
      // 验证token
      console.log('🔑 Validating token...');
      const isValid = await appleAuthService.validateToken(authData.authorization.id_token);
      console.log('🔑 Token validation result:', isValid);
      
      if (isValid) {
        console.log('🔑 Setting user data...');
        appleAuthService.setUser(authData.user, authData.authorization.id_token);
        console.log('🔑 Login successful!');
        return { success: true };
      } else {
        throw new Error('Invalid token');
      }
    } catch (error) {
      console.error('🔑 Login failed:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    appleAuthService.clearUser();
  };

  const submitVote = async (voteData) => {
    try {
      const result = await appleAuthService.submitVote(voteData);
      return { success: true, data: result };
    } catch (error) {
      console.error('Vote submission failed:', error);
      return { success: false, error: error.message };
    }
  };

  const getVoteResults = async (voteId) => {
    try {
      const results = await appleAuthService.getVoteResults(voteId);
      return results;
    } catch (error) {
      console.error('Failed to get vote results:', error);
      return null;
    }
  };

  const value = {
    user,
    token,
    loading,
    isLoggedIn: !!user && !!token,
    login,
    logout,
    submitVote,
    getVoteResults
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
